import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../lib/api'
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ProxyLeaveApplyModal from './ProxyLeaveApplyModal';
import './Application.css';

// Component Custom Dropdown giúp hiển thị số màu đỏ & căn lề phải thẳng hàng tuyệt đối
const CustomCategorySelect = ({ category, setCategory, categoryLabels, pendingCounts }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedCount = pendingCounts[category] || 0;

  return (
    <div className="custom-dropdown-container" ref={dropdownRef}>
      <div 
        className="custom-dropdown-header" 
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="dropdown-label-center">{categoryLabels[category]}</span>
        {selectedCount > 0 && (
          <span className="dropdown-count-red">{selectedCount}</span>
        )}
        <span className="dropdown-arrow">▼</span>
      </div>

      {isOpen && (
        <div className="custom-dropdown-list">
          {Object.entries(categoryLabels).map(([key, label]) => {
            const count = pendingCounts[key] || 0;
            const isSelected = key === category;

            return (
              <div
                key={key}
                className={`custom-dropdown-item ${isSelected ? "selected" : ""}`}
                onClick={() => {
                  setCategory(key);
                  setIsOpen(false);
                }}
              >
                <span className="item-label">{label}</span>
                <span className="item-count">{count > 0 ? count : ""}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// 有給日の表示用：連続する日付は「開始〜終了」、飛んでいる日付は別々に表示。
// 例: ["2026-06-01","2026-06-02","2026-06-04"] → "2026-06-01〜2026-06-02、2026-06-04"
const formatLeaveDates = (dates) => {
  const valid = (Array.isArray(dates) ? dates : [])
    .map((d) => String(d || '').trim())
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
  if (valid.length === 0) return '';
  const sorted = Array.from(new Set(valid)).sort();
  const toTime = (d) => new Date(`${d}T00:00:00`).getTime();
  const ONE_DAY = 86400000;
  const groups = [];
  let startStr = sorted[0];
  let prevStr = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    if (toTime(cur) - toTime(prevStr) === ONE_DAY) {
      prevStr = cur; // 連続
    } else {
      groups.push(startStr === prevStr ? startStr : `${startStr}〜${prevStr}`);
      startStr = cur;
      prevStr = cur;
    }
  }
  groups.push(startStr === prevStr ? startStr : `${startStr}〜${prevStr}`);
  return groups.join('、');
};

const CATEGORY_LABELS = {
  OVERTIME: "残業",
  LATE_IN: "遅刻",
  EARLY_OUT: "早退",
  joined: "入社届",
  VACATION: "有給休暇",
  HOLIDAY_WORK: "休日出勤",
  ADDRESS: "住所変更",
  NAME_CHANGE: "氏名変更",
  BANK: "振込先変更",
  COMMUTE: "交通機関",
  FAMILY: "家族異動",
  REMOVE: "異動年月日",
  RETIREMENT: "退職届",
  MYNUMBER: "マイナンバー",
  SPECIAL_LEAVE: "特別休暇申請",
  SERI: "生理休暇",
  TRANSPORT_ROUND: "日帰り申請",
  ALLOWANCE: "諸手当",
};

const STATUS_LABELS = {
  SUBMITTED: "申請中",
  APPROVED: "承 認",
  REJECTED: "未承認",
  CANCELLED: "取消一覧",
};

const SPECIAL_LEAVE_REASONS = {
  BEREAVEMENT: "忌引(家族の不幸)",
  MARRIAGE: "結婚",
  CHILDBIRTH: "出産",
  CHILDCARE: "子供看護",
  KAIGO: "介護",
  FRESH: "リフレッシュ",
  NEW_YEAR: "年末年始",
  SERI: "生理休暇",
  BIRTHDAY: "バースデイ休暇",
  OTHER: "その他",
};

// 数値を 3 桁コンマ区切り + 円表記に変換（例: 9480 → "9,480 円"）
const formatYen = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const n = Number(String(val).replace(/[^0-9.-]/g, ''));
  if (Number.isNaN(n)) return String(val);
  return `${n.toLocaleString('ja-JP')} 円`;
};

// HH:MM 形式の時間文字列を小数時間に変換（例: "00:15" → "0.25"）
const toDecimalHours = (timeStr) => {
  if (!timeStr) return '';
  const parts = String(timeStr).split(':');
  if (parts.length !== 2) return timeStr;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return timeStr;
  const total = h + m / 60;
  if (m === 0) return String(total);
  return parseFloat(total.toFixed(2)).toString();
};


const Application = () => {
  const [applications, setApplications] = useState([]);
  const [showProxyModal, setShowProxyModal] = useState(false);
  const [category, setCategory] = useState("VACATION");
  const [statusFilter, setStatusFilter] = useState("SUBMITTED");
  const navigate = useNavigate();


  const loadApplications = useCallback(async () => {
    try {
      const res = await api.get(`/admin/applications/?_t=${new Date().getTime()}`);
      setApplications(res.data);
    } catch (err) {
      console.error("申請情報の読み込みエラー:", err);
    }
  }, []);

  const location = useLocation();
  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  /** 申請書の種類ごとの「申請中」件数。選択肢の後ろに ①②… で表示する。
   *  カテゴリは大文字小文字が混在する（joined など）ため、ラベル定義のキーに合わせて数える。 */
  const pendingCountByCategory = useMemo(() => {
    const counts = {};
    (applications || []).forEach((app) => {
      if (app.status !== "SUBMITTED") return;
      const raw = String(app.category || "");
      const key = Object.keys(CATEGORY_LABELS).find(
        (k) => k.toUpperCase() === raw.toUpperCase()
      );
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [applications]);

  // 詳細画面から戻ったときに一覧を再取得（BUG-001: 申請承認後リアルタイムで申請中から消す）
  useEffect(() => {
    if (location.state?.refreshList) {
      loadApplications();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refreshList, location.pathname, loadApplications, navigate]);

  // 申請のステータスを更新する関数
  const updateStatus = async (id, newStatus) => {

    let reason = "";
    if (newStatus === "REJECTED") {
      reason = prompt("未承認の理由を入力してください:");
      if (!reason) return;
    }
    if (newStatus === "RETURNED") {
      reason = prompt("差し戻しの理由を入力してください（社員に通知されます）:");
      if (!reason) return;
    }

    try {
      await api.post(`/admin/applications/${id}/status/`, {
        status: newStatus,
        reject_reason: reason,
      });
      await loadApplications();
      alert("申請ステータスを更新しました。");
    } catch (err) {
      console.error("申請ステータスの更新エラー:", err);
      alert("申請ステータスの更新に失敗しました。");
    }
  };

  // 対象日ごとに独立して成立する申請種別。申請日が違えば別の申請なので、
  // 「承認済があるから隠す」の対象にしてはならない（バックエンドの DATE_SCOPED_CATEGORIES と対応）。
  const DATE_SCOPED_CATEGORIES = new Set([
    "TRANSPORT_ROUND", "EXPENSE", "VACATION", "HOLIDAY_WORK", "SPECIAL_LEAVE",
    "SERI", "OVERTIME", "LATE_IN", "EARLY_OUT",
  ]);

  // 承認済みの「社員コード-カテゴリ」キーのセット（カテゴリは大文字に正規化）
  const approvedKeys = new Set(
    applications
      .filter(app => app.status === "APPROVED")
      .map(app => {
        const v = String(app.category || "").toUpperCase();
        const normalized = v === "RESIGN" ? "RETIREMENT" : v;
        return `${app.applicant_memberId}-${normalized}`;
      })
  );

  // 入社届は DB 上 "joined"（小文字）で保存される一方、フロントの選択肢キーは "JOINED"。
  // 退職届も DB 上は "RESIGN" のことがあるため、カテゴリは大文字小文字を無視し
  // RETIREMENT と RESIGN を同一視して比較する。
  const normalizeCategory = (c) => {
    const v = String(c || "").toUpperCase();
    if (v === "RESIGN") return "RETIREMENT";
    return v;
  };

  // 「取消一覧」タブは全カテゴリ横断で取消済み申請を表示する
  const isCancelledView = statusFilter === "CANCELLED";

  // フィルタリングされた申請リストを取得
  const filtered = applications.filter((app) => {
    // 取消一覧はカテゴリを問わず CANCELLED をすべて表示
    if (isCancelledView) {
      return app.status === "CANCELLED";
    }
    // 退職は未承認タブを非表示
    if (category === "RETIREMENT" && statusFilter === "REJECTED") {
      return false;
    }
    // 未承認（REJECTED）タブ: 同じ社員+カテゴリで承認済がある場合は非表示。
    // ただし日付ごとに独立する種別は、別日の承認済があっても隠さない。
    if (statusFilter === "REJECTED" && !DATE_SCOPED_CATEGORIES.has(normalizeCategory(app.category))) {
      const key = `${app.applicant_memberId}-${normalizeCategory(app.category)}`;
      if (approvedKeys.has(key)) return false;
    }
    return (
      normalizeCategory(app.category) === normalizeCategory(category) &&
      app.status === statusFilter
    );
  });

  // -------------------------------
  // レンダリングする列を決定する関数
  // -------------------------------
  const renderColumns = () => {
    switch (category) {
      case "OVERTIME":
        return (
          <>
            <th className='wide-col-date'>残業日</th>
            <th className="wide-col">開始</th>
            <th className="wide-col">終了</th>
            <th className="wide-col">休憩</th>
            <th className="wide-col">合計</th>
            <th className="wide-col-reason">理由</th>
            <th className="wide-col-reason">超過／早期終了の理由</th>
          </>
        );
      case "LATE_IN":
        return (
          <>
            <th className='wide-col-date'>発生日</th>
            <th className="wide-col">遅刻時間</th>
            <th className="wide-col-name">添付ファイル</th>
            <th className="wide-col-reason">理由</th>
          </>
        );
      case "EARLY_OUT":
        return (
          <>
            <th className='wide-col-date'>発生日</th>
            <th className="wide-col">早退時間</th>
            <th className="wide-col-reason">理由</th>
          </>
        );
      case "joined":
        return (
          <>
            <th className='wide-col-date'>入社日</th>
            <th className="wide-col">フリガナ</th>
            <th className='wide-col'>生年月日</th>
            <th>郵便番号</th>
            <th className='wide-col'>住所</th>
            <th className="wide-col">連絡先1</th>
            <th className='wide-col'>連絡先2</th>
            <th className="wide-col">銀行名</th>
            <th className='wide-col'>支店名</th>
            <th className="wide-col">支店名カナ</th>
            <th className='wide-col'>口座番号</th>
            <th className="wide-col">交通機関名</th>
            <th className="wide-col">路線名</th>
            <th className='wide-col'>乗車駅名～下車駅名</th>
            <th className="wide-col">定期代金</th>
          </>
        )
      case "VACATION":
        return (
          <>
            <th className='wide-col-date'>有給日</th>

            <th className="wide-col-reason">理由</th>
          </>
        );

      /* ===== 休日出勤 (HOLIDAY_WORK) ヘッダー定義 ===== */
      case "HOLIDAY_WORK":
        return (
          <>
            <th className='wide-col-date'>出勤予定日</th>
            <th className="wide-col">区分</th>
            <th className='wide-col-date'>振替日</th>
            <th className="wide-col">勤務予定時間</th>
            <th className="wide-col">休憩時間</th>
            <th className="wide-col-reason">申請理由</th>
          </>
        );

      case "SERI":
        return (
          <>
            <th className="wide-col-content">内容</th>
            <th className='wide-col-date'>生休日</th>
            <th className="wide-col">日数</th>
          </>
        );

      case "REMOVE":
        return (
          <>
            <th className='wide-col-date'>異動年月日</th>
            <th className="wide-col-reason">理由</th>
          </>
        );


      case "ADDRESS":
      case "NAME_CHANGE":
        return null;

      case "BANK":
        return (
          <>
            <th className="wide-col">銀行名</th>
            <th className="wide-col">支店名</th>
            <th className="wide-col">支店名(カナ)</th>
            <th className="wide-col">口座番号</th>
            <th className="wide-col-date">希望適合日</th>
          </>
        );

      case "COMMUTE":
        return (
          <>
            <th className="wide-col">交通機関名</th>
            <th className="wide-col">路線名</th>
            <th className="wide-col">乗車駅名～下車駅名</th>
            <th className="wide-col">定期代金</th>
            <th className="wide-col">合計定期代金</th>
          </>
        );

      case "FAMILY":
        return (
          <>
            <th>区分</th>
            <th>家族異動</th>
            <th>家族数</th>
          </>
        );

      case "RETIREMENT":
        return (
          <>
            <th className='wide-col-date'>申請日</th>
            <th className='wide-col-date'>退職日</th>
            <th className="wide-col-reason">理由</th>
          </>
        );

      case "MYNUMBER":
        return (
          <>
            <th className='wide-col-code'>マイナンバー</th>
            <th className="wide-col-name">添付ファイル</th>
          </>
        );
      
      case "SPECIAL_LEAVE":
        return (
          <>
            <th className='wide-col-date'>開始日</th>
            <th className='wide-col-date'>終了日</th>
            <th className="wide-col-reason">理由</th>
          </>
        );

      case "TRANSPORT_ROUND":
        return (
          <>
            <th className='wide-col-date'>日付</th>
            <th className="wide-col-reason">出張用件</th>
            <th className="wide-col">出張時間</th>
            <th className="wide-col">利用交通機関</th>
            <th className="wide-col">金額</th>
          </>
        );

      case "ALLOWANCE":
        return (
          <>
            <th className="wide-col">家族手当</th>
            <th className="wide-col">住宅手当</th>
            <th className="wide-col">派遣手当</th>
            <th className="wide-col">都市手当</th>
            <th className="wide-col">資格手当</th>
            <th className="wide-col">合計</th>
          </>
        );

      default:
        return null;
    }
  };

  // -------------------------------
  // row データを決定する関数
  // -------------------------------
  const renderRow = (formData, app) => {
  
    if (app.category === "SERI") {
      const selectedDates = formData?.selectedDates || [];
      const totalDays = formData?.totalDays || selectedDates.length || 1;

      return (
        <>
          <td className="wide-col-content" style={{ fontWeight: '500' }}>生理休暇</td>
          <td className="wide-col-date">{selectedDates.join(', ')}</td>
          <td className="small-col text-center">
            <span className="badge bg-success">{totalDays} 日</span>
          </td>
        </>
      );
    }
    if (!formData || !app) return null;
    switch (category) {
      case "OVERTIME":
        return (
          <>
            <td className='wide-col-date'>{formData.overtime_date}</td>
            <td className="wide-col">{formData.scheduled_end || '—'}</td>
            <td className="wide-col">{formData.overtime_end || '—'}</td>
            <td className="wide-col">{toDecimalHours(formData.break_hours)}</td>
            <td className="wide-col">{toDecimalHours(formData.overtime_hours)}</td>
            <td className="wide-col-reason">{formData.reason}</td>
            <td className="wide-col-reason">{formData.deviation_reason || '—'}</td>
          </>
        );
      case "LATE_IN":
        return (
          <>
            <td className='wide-col-date'>{formData.late_date}</td>
            <td className="wide-col">{toDecimalHours(formData.late_hours)}</td>
            <td className="wide-col-name">{app.attachment ? (
              <a
                href={`${app.attachment}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#007bff', textDecoration: 'underline', fontWeight: 'bold' }}
              >
                🖼️ 画像を表示
              </a>
            ) : null}</td>
            <td className="wide-col-reason">{formData.reason}</td>
          </>
        );
      case "EARLY_OUT":
        return (
          <>
            <td className='wide-col-date'>{formData.early_out_date}</td>
            <td className="wide-col">{toDecimalHours(formData.early_out_hours)}</td>
            <td className="wide-col-reason">{formData.reason}</td>
          </>
        );
      case "joined":
      case "JOINED": {
        const joinedStartDate = formData.startDate || formData.start_date || '';
        const joinedNameFuri = formData.nameFuri || formData.namefuri || '';
        const joinedBirthDate = formData.birthDate || formData.birthdate || '';
        const extras = Array.isArray(formData.extraTrains) ? formData.extraTrains : [];
        const allTrains = [
          {
            transport: formData.transport || '',
            road: formData.road || '',
            station: formData.station || '',
            fare: formData.fare,
          },
          ...extras,
        ];
        const renderMulti = (key, fmt = (v) => v) => (
          <td className="wide-col">
            {allTrains.map((t, i) => (
              <div key={i}>{fmt(t[key])}</div>
            ))}
          </td>
        );
        return (
          <>
            <td className='wide-col-date'>{joinedStartDate}</td>
            <td className="wide-col">{joinedNameFuri}</td>
            <td className='wide-col'>{joinedBirthDate}</td>
            <td>{formData.zipCode}</td>
            <td className='wide-col'>{formData.address}</td>
            <td className="wide-col">{formData.contact1}</td>
            <td className='wide-col'>{formData.contact2}</td>
            <td className="wide-col">{formData.bankName}</td>
            <td className='wide-col'>{formData.branchName}</td>
            <td className="wide-col">{formData.branchNameKana}</td>
            <td className='wide-col'>{formData.accountNumber}</td>
            {renderMulti('transport')}
            {renderMulti('road')}
            {renderMulti('station')}
            {renderMulti('fare', formatYen)}
          </>
        );
      }
      case "VACATION": {
        let dateDisplay = '';
        if (Array.isArray(formData.selectedDates) && formData.selectedDates.length > 0) {
          dateDisplay = formatLeaveDates(formData.selectedDates);
        } else if (formData.start_date && formData.end_date && formData.start_date !== formData.end_date) {
          dateDisplay = `${formData.start_date} 〜 ${formData.end_date}`;
        } else {
          dateDisplay = formData.start_date || formData.selectedDate || formData.application_date || formData.applicationDate || '';
        }
        return (
          <>
            <td className='wide-col-date'>{dateDisplay}</td>
            <td className="wide-col-reason">{formData.reason}</td>
          </>
        );
      }

      case "HOLIDAY_WORK": {
        let dateDisplay = '';
        if (Array.isArray(formData.selectedDates) && formData.selectedDates.length > 0) {
          dateDisplay = formatLeaveDates(formData.selectedDates);
        } else {
          dateDisplay = formData.work_date || formData.selectedDate || formData.targetDate || '';
        }
        const workTime = (formData.workStartTime && formData.workEndTime)
          ? `${formData.workStartTime} 〜 ${formData.workEndTime}`
          : (formData.startTime && formData.endTime) ? `${formData.startTime} 〜 ${formData.endTime}` : '—';

        return (
          <>
            <td className='wide-col-date'>{dateDisplay}</td>
            <td className="wide-col">{formData.isFurikae ? '振替休日' : '通常'}</td>
            <td className='wide-col-date'>
              {formData.isFurikae
                ? ((formData.substituteDates || []).join(', ') || formData.substituteDate || '—')
                : '—'}
            </td>
            <td className="wide-col">{workTime}</td>
            <td className="wide-col">{toDecimalHours(formData.breakTime || formData.break_hours) || '—'}</td>
            <td className="wide-col-reason">{formData.reason || '—'}</td>
          </>
        );
      }

      case "REMOVE":
        return (
          <>
            <td className='wide-col-date'>{formData.application_date}</td>
            <td className="wide-col-reason">{formData.reason}</td>
          </>
        );

      case "ADDRESS":
        return (
          <>
            <td>{formData.oldZipCode}</td>
            <td className="wide-col">{formData.oldAddress}</td>
            <td className="wide-col">{formData.oldAddressKana}</td>
            <td >{formData.newZipCode}</td>
            <td className="wide-col">{formData.newAddress}</td>
            <td className="wide-col">{formData.newAddressKana}</td>
          </>
        );

      case "NAME_CHANGE":
        return (
          <>
            <td>{formData.oldName}</td>
            <td>{formData.oldKanaName}</td>
            <td>{formData.newName}</td>  
            <td>{formData.newKanaName}</td>
          </>
        );

      case "BANK":
        return (
          <>
            <td>{formData.bankName}</td>
            <td>{formData.branchName}</td>
            <td>{formData.branchNameKana}</td>
            <td>{formData.accountNumber}</td>
            <td className='wide-col-date'>{formData.desiredDate}</td>
          </>
        );

        case "COMMUTE":
          const routes = formData?.routes || [];
          
          const getSearchLink = (from, to) => {
            if (!from || !to) return null;
            return `https://transit.yahoo.co.jp/search/result?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
          };
        
          return (
            <>
              <td>
                {routes.length > 0 ? routes.map((r, i) => (
                  <div key={i}>{r.transportName || "—"}</div>
                )) : "—"}
              </td>
              <td>
                {routes.length > 0 ? routes.map((r, i) => (
                  <div key={i}>{r.route || "—"}</div>
                )) : "—"}
              </td>
              <td>
                {routes.length > 0 ? routes.map((r, i) => {
                  const searchUrl = getSearchLink(r.stationFrom, r.stationTo);
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      {r.stationFrom} ～ {r.stationTo}
                      {searchUrl && (
                        <a 
                          href={searchUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          title="Kiểm tra tiền tàu thực tế"
                          style={{ textDecoration: 'none', fontSize: '14px' }}
                        >
                          🔍
                        </a>
                      )}
                    </div>
                  );
                }) : "—"}
              </td>
              <td style={{ textAlign: 'right' }}>
                {routes.length > 0 ? routes.map((r, i) => (
                  <div key={i}>{Number(r.fare).toLocaleString()} 円</div>
                )) : "—"}
              </td>
              <td style={{ fontWeight: 'bold', color: '#d32f2f', textAlign: 'right' }}>
                {Number(formData.totalFare || 0).toLocaleString()} 円
              </td>
            </>
          );

      case "FAMILY":
        const members = formData.familyMembers || []; 
        return (
          <>
            <td>{formData.changeType}</td>
            <td>
              {members.map((member, index) => (
                <div key={index} className="family-member-info">
                  {member.relation}: {member.nameKanji} ({member.nameKana})
                </div>
              ))}
            </td>
            <td>{formData.familyMembersCount}</td>
          </>
        );
      
      case "RETIREMENT":
        return (
          <>
            <td className='wide-cod-date'>{formData.applicationDate || formData.application_date || ''}</td>
            <td className='wide-cod-date'>{formData.end_date || formData.endDate || formData.resignDate || ''}</td>
            <td className="wide-col-reason">{formData.reason}</td>
          </>
        );

      case "MYNUMBER":
        return (
          <>
            <td className='wide-col-code'>{formData.myNumber}</td>
            <td className="wide-col-name">{app.attachment ? (
              <a 
                href={`${app.attachment}`} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#007bff', textDecoration: 'underline', fontWeight: 'bold' }}
              >
                🖼️ 画像を表示
              </a>
            ) : (
              <span className="text-muted">写真なし </span>
            )}</td>
          </>
        );
      
      case "SPECIAL_LEAVE":
        return (
          <>
            <td className='wide-col-date'>{formData.start_date}</td>
            <td className='wide-col-date'>{formData.end_date}</td>
            <td className="wide-col-reason">{SPECIAL_LEAVE_REASONS[formData.reason] || formData.reason}</td>
          </>
        );

      case "TRANSPORT_ROUND": {
        const trRoutes = Array.isArray(formData.routes) ? formData.routes : [];
        return (
          <>
            <td className='wide-col-date'>{formData.targetDate}</td>
            <td className="wide-col-reason">{formData.businessPurpose}</td>
            <td className="wide-col">{formData.businessStartTime && formData.businessEndTime ? `${formData.businessStartTime} ～ ${formData.businessEndTime}` : (formData.businessStartTime || formData.businessEndTime || '')}</td>
            <td className="wide-col">
              {trRoutes.length > 0 ? trRoutes.map((r, i) => (
                <div key={i}>
                  {r.transportName} {r.stationFrom}～{r.stationTo} {(Number(r.fare) || 0).toLocaleString('ja-JP')}円{r.roundTrip ? '（往復）' : ''}
                </div>
              )) : (formData.trainName || '')}
            </td>
            <td className="wide-col">{formData.amount ? `${Number(formData.amount).toLocaleString('ja-JP')}円` : ''}</td>
          </>
        );
      }

      case "ALLOWANCE": {
        const yen = (v) => (Number(v) ? `${Number(v).toLocaleString('ja-JP')}円` : '—');
        return (
          <>
            <td className="wide-col">{yen(formData.familyAllowance)}</td>
            <td className="wide-col">{yen(formData.housingAllowance)}</td>
            <td className="wide-col">{yen(formData.dispatchAllowance)}</td>
            <td className="wide-col">{yen(formData.cityAllowance)}</td>
            <td className="wide-col">{yen(formData.qualificationAllowance)}</td>
            <td className="wide-col">{yen(formData.totalAllowance)}</td>
          </>
        );
      }

      default:
        return null;
    }
  };

  useEffect(() => {
    if (applications.length > 0) {
      console.log("COMMUTE formData:", applications[0].formData);
    }
  }, [applications]);

  return (
    <div className="application-container">
      <div className="application-header">
        <h2 className="application-title">申請管理</h2>
        
        <button className="application-back-btn" onClick={() => navigate('/dashboard')}>
            メニュー画面に戻る
        </button>
      </div>
      
      {/* ---------------------- */}
      {/* Control Area */}
      {/* ---------------------- */}
      <div className="filters">
        <h3>申請書</h3>

        {/* Custom Dropdown thay thế cho <select> cũ */}
        <CustomCategorySelect
          category={category}
          setCategory={setCategory}
          categoryLabels={CATEGORY_LABELS}
          pendingCounts={pendingCountByCategory}
        />

        {/* ステータス選択（タブUI） */}
        <div className="status-tabs">
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`status-tab ${statusFilter === key ? "status-tab-active" : ""}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ---------------------- */}
      {/* Table */}
      {/* ---------------------- */}
      <div className="app-section-label">
        申請一覧
        {category === "VACATION" && !isCancelledView && (
          <button
            type="button"
            className="app-proxy-btn"
            onClick={() => setShowProxyModal(true)}
          >
            ＋ 代理申請
          </button>
        )}
      </div>
      {showProxyModal && (
        <ProxyLeaveApplyModal
          onClose={() => setShowProxyModal(false)}
          onCreated={() => {
            loadApplications();
            alert("代理で申請を登録しました。内容を確認して承認してください。");
          }}
        />
      )}
      <div className="table-scroll-wrapper">
        <table className={`table table-bordered table-hover mt-3 category-${category}`}>
          
          <thead className="table-dark">
            <tr>
              <th className='wide-col-code' rowSpan={!isCancelledView && (category === "ADDRESS" || category === "NAME_CHANGE") ? 2 : 1}>社員コード</th>
              <th className='wide-col-name' rowSpan={!isCancelledView && (category === "ADDRESS" || category === "NAME_CHANGE") ? 2 : 1}>申請者</th>
              {isCancelledView ? (
                <>
                  <th className="wide-col">申請区分</th>
                  <th className="wide-col-reason">内容</th>
                </>
              ) : category === "ADDRESS" ? (
                <>
                  <th colSpan={3} className="info-group-header">旧情報</th>
                  <th colSpan={3} className="info-group-header">新情報</th>
                </>
              ) : category === "NAME_CHANGE" ? (
                <>
                  <th colSpan={2} className="info-group-header">旧情報</th>
                  <th colSpan={2} className="info-group-header">新情報</th>
                </>
              ) : (
                renderColumns()
              )}
              <th rowSpan={!isCancelledView && (category === "ADDRESS" || category === "NAME_CHANGE") ? 2 : 1}>ステータス</th>
            </tr>
            {!isCancelledView && (category === "ADDRESS" || category === "NAME_CHANGE") && (
              <tr>
                {category === "ADDRESS" ? (
                  <>
                    <th>旧郵便番号</th>
                    <th className="wide-col">旧住所(漢字)</th>
                    <th className="wide-col">旧住所(カナ)</th>
                    <th>新郵便番号</th>
                    <th className="wide-col">新住所(漢字)</th>
                    <th className="wide-col">新住所(カナ)</th>
                  </>
                ) : (
                  <>
                    <th className="wide-col">旧氏名</th>
                    <th className="wide-col">旧氏名(カナ)</th>
                    <th className="wide-col">新氏名</th>
                    <th className="wide-col">新氏名(カナ)</th>
                  </>
                )}
              </tr>
            )}
          </thead>

          <tbody>
            {filtered.length === 0 ? (
              <tr>
              </tr>
            ) : (
              filtered.map((app) => (
                <tr key={app.id}>
                  <td className='wide-col-code'>
                    <button
                      type="button"
                      className="app-code-link"
                      onClick={() => navigate(`/admin/applications/${app.id}`)}
                    >
                      {app.applicant_memberId}
                    </button>
                  </td>
                  <td className='wide-col-name'>{app.applicant_name}</td>

                  {isCancelledView ? (
                    <>
                      <td className="wide-col">{CATEGORY_LABELS[app.category] || app.category}</td>
                      <td className="wide-col-reason">{app.title || app.content || "-"}</td>
                    </>
                  ) : (
                    renderRow(app.formData, app)
                  )}

                  <td className="small-col">
                    {isCancelledView ? (
                      <span className="badge bg-secondary">取消済</span>
                    ) : (
                      <select
                        className="form-select status-dropdown"
                        value={app.status}
                        onChange={(e) => updateStatus(app.id, e.target.value)}
                      >
                        <option value="SUBMITTED">申請中</option>
                        <option value="APPROVED">承 認</option>
                        {category !== "RETIREMENT" && (
                          <option value="REJECTED">未承認</option>
                        )}
                        <option value="RETURNED">差し戻し</option>
                      </select>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
    </div>
  );

};
export default Application;